// Helpers for whole-number currency amounts (IQD) typed into text inputs.
//
// The value kept in component state / sent to the API is always a plain digit
// string ("12000"); thousand separators only ever exist in what the input
// displays ("12,000").

// Arabic-Indic (U+0660–U+0669) and Extended Arabic-Indic / Persian
// (U+06F0–U+06F9) digits, typed from an Arabic keyboard, mapped to ASCII.
const toAsciiDigits = (str) =>
  str
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

const stripLeadingZeros = (digits) => digits.replace(/^0+(?=\d)/, "");

/**
 * Raw user input -> digit-only string. Drops letters, symbols, spaces,
 * separators and leading zeros (a lone "0" is kept).
 *   "12,0a00" -> "12000",  "007" -> "7",  "" -> ""
 */
export const sanitizeAmountInput = (raw) =>
  stripLeadingZeros(toAsciiDigits(String(raw ?? "")).replace(/\D/g, ""));

/**
 * Any stored amount (number, "20000", "20000.00" from the API, "20,000")
 * -> whole-number digit string. Used for display, so existing amounts render
 * correctly when a row is opened for editing.
 */
export const toAmountDigits = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(Math.round(Math.abs(value))) : "";
  }
  const str = toAsciiDigits(String(value)).replace(/,/g, "").trim();
  // Decimal strings such as "20000.00" (DECIMAL columns) -> "20000".
  if (/^\d*\.\d*$/.test(str) && /\d/.test(str)) {
    return String(Math.round(Number(str)));
  }
  return sanitizeAmountInput(str);
};

/**
 * Amount -> display string with comma thousand separators.
 *   100 -> "100", 1000 -> "1,000", "1000000" -> "1,000,000", "" -> ""
 */
export const formatAmountInput = (value) =>
  toAmountDigits(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * Money amount for DISPLAY (invoices, totals, price columns): rounded to a
 * whole number with comma separators. IQD has no minor unit, so no decimal
 * point is ever shown. Display only; never use the result in calculations.
 *   1200.3 -> "1,200",  "20000.00" -> "20,000",  "1,500" -> "1,500",
 *   null / "" / "abc" -> "0"
 */
export const formatWholeAmount = (value) => {
  const n =
    typeof value === "number"
      ? value
      : parseFloat(toAsciiDigits(String(value ?? "")).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "0";
  // `+ 0` turns -0 (e.g. Math.round(-0.4)) into 0 so it never renders "-0".
  return (Math.round(n) + 0).toLocaleString("en-US");
};

/**
 * Percentage for display without a trailing ".0" (6 / 6.0 / "6.00" -> "6").
 * A real fraction set by the admin is kept (6.5 -> "6.5") so the rate is not
 * misstated.
 */
export const formatPercent = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : "0";
};

/** Number of digits in `str` (ASCII or Arabic-Indic). */
export const countAmountDigits = (str) =>
  toAsciiDigits(String(str ?? "")).replace(/\D/g, "").length;

/**
 * Caret index in a formatted string that sits right after the
 * `digitCount`-th digit. Keeps the caret next to the digit the user just
 * typed/deleted when separators are added or removed around it.
 */
export const caretIndexAfterDigits = (formatted, digitCount) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (formatted[i] >= "0" && formatted[i] <= "9") {
      seen += 1;
      if (seen === digitCount) return i + 1;
    }
  }
  return formatted.length;
};
